import { RefreshCw, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import accountSwitchIcon from '@/assets/icons/accountSwitch.svg';
import greyAlertIcon from '@/assets/icons/greyAlert.svg';
import redProfileIcon from '@/assets/icons/redProfile.svg';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import posthog from '@/config/analytics/posthog';
import { signOutDevice } from '@/config/auth/deviceToken';
import { logoutMsal } from '@/config/auth/msal';
import { clearSentryUser } from '@/config/monitoring/sentry';
import { useConnectMailbox } from '@/features/auth/hooks/useConnectMailbox';
import { clearDismissedMismatch, useAuthStore } from '@/utils/stores/useAuthStore';

function MailboxRow({
  email,
  highlighted,
  icon,
  label,
}: {
  email: string;
  highlighted?: boolean;
  icon: string;
  label: string;
}) {
  return (
    <div
      className={`flex w-full items-center justify-between gap-2 ${
        highlighted ? 'border-highlight rounded-lg border px-3 py-2' : ''
      }`}
    >
      <div className="flex flex-col gap-0.5">
        <p className="text-tertiary text-[11px] leading-[160%] font-medium">{label}</p>
        <p className="text-primary max-w-40 truncate text-xs leading-[160%] font-medium">{email}</p>
      </div>
      <img alt="" aria-hidden className="shrink-0" src={icon} />
    </div>
  );
}

/**
 * Shown when the Outlook host mailbox is not the mailbox connected to the
 * signed-in Lucio account. Remedies, in order of likelihood:
 *  - "Use this mailbox": switch the account's connection to the host mailbox
 *    (connect-mailbox with replaceExisting — only displaces the caller's own
 *    previous mailbox; a mailbox owned by another account still 409s and the
 *    masked-owner message is surfaced inline).
 *  - "Log out": a different human owns the host mailbox (shared machine).
 *  - "Continue anyway": dismiss — with the caveat that actions on the open
 *    email won't work, since the backend reads mail with the CONNECTED
 *    mailbox's Graph credential.
 */
// Cognitive score is JSX nesting on a three-button card, not logic.
// fallow-ignore-next-line complexity
export const MailboxMismatchScreen = () => {
  const { t } = useTranslation();
  const { accountMismatch, dismissCurrentMismatch, setAccountMismatch } = useAuthStore();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const connectMutation = useConnectMailbox((message) => setErrorMessage(message), {
    onSuccess: () => setAccountMismatch(null),
    replaceExisting: true,
  });

  const handleLogout = async () => {
    clearDismissedMismatch();
    // Drop the analytics identity up front. distinct_id and the registered
    // `user_email` super property live in localStorage, so the reload below
    // would otherwise restore them and file the next person's login-screen
    // events under the previous one — the very case this screen exists for.
    // Done before the awaits, not after: the local sign-out is unconditional,
    // so clearing the identity that mirrors it must be too.
    posthog.reset();
    clearSentryUser();
    try {
      // Full sign-out: the host mailbox belongs to a different person, so the
      // whole session (device token + cached MSAL account) has to go before
      // they sign in fresh.
      await signOutDevice();
      await logoutMsal();
    } finally {
      // Reload only after logout settles, so a mid-logout failure doesn't
      // strand the user with a half-cleared cache.
      window.location.reload();
    }
  };

  if (!accountMismatch) {
    return null;
  }

  return (
    <div className="bg-background/25 fixed inset-0 z-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div className="bg-card flex flex-col items-center justify-center gap-5 rounded-lg p-5 text-left">
          <img alt="" aria-hidden className="icon-themed" src={greyAlertIcon} />
          <p className="text-primary text-sm font-semibold break-all">{t('mismatch.title')}</p>

          <MailboxRow
            email={accountMismatch.hostMailbox}
            highlighted
            icon={accountSwitchIcon}
            label={t('mismatch.viewing')}
          />

          <MailboxRow
            email={accountMismatch.connectedMailbox}
            icon={redProfileIcon}
            label={t('mismatch.connected')}
          />

          {errorMessage && (
            <Alert className="w-full" data-testid="error-alert" variant="destructive">
              <TriangleAlert className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <Button
            className="w-full gap-2"
            disabled={connectMutation.isPending}
            loading={connectMutation.isPending}
            onClick={() => {
              setErrorMessage(null);
              connectMutation.mutate();
            }}
          >
            <RefreshCw className="h-4 w-4" />
            {connectMutation.isPending ? t('mismatch.switching') : t('mismatch.useThisMailbox')}
          </Button>

          <Button
            className="w-full"
            disabled={connectMutation.isPending}
            onClick={handleLogout}
            variant="outline"
          >
            {t('mismatch.logout')}
          </Button>

          <div className="flex w-full flex-col items-center gap-1">
            <Button className="w-full" onClick={dismissCurrentMismatch} variant="ghost">
              {t('mismatch.continueAnyway')}
            </Button>
            <p className="text-tertiary m-0 p-0 text-center text-[11px] leading-[160%]">
              {t('mismatch.caveat')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
